import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";

import { Adg4Button } from "@/components/adg4/button/src/button";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";

export interface EnterpriseHeroProps {
    headerText: string;
    bodyText: string;
    textAlign?: "center" | "left" | "right";
    color?: "light" | "dark";
    cta?:
        | {
              text: string;
              href: string;
              dataEventProps: DataEventProps;
          }
        | JSX.Element;
    ctaType?: "button" | "link";
    backgroundImgSrc?: {
        xs: string;
        sm: string;
        md: string;
        lg: string;
    };
}

const contentMaxWidth = {
    center: {
        xs: "378px",
        sm: "534px",
        md: "568px",
        lg: "568px"
    },
    left: {
        xs: "378px",
        sm: "421px",
        md: "514px",
        lg: "568px"
    },
    right: {
        xs: "378px",
        sm: "365px",
        md: "514px",
        lg: "668px"
    }
};

export const EnterpriseHero = ({
    headerText,
    bodyText,
    textAlign = "center",
    color = "light",
    cta,
    ctaType,
    backgroundImgSrc
}: // eslint-disable-next-line complexity
EnterpriseHeroProps) => {
    const textColor =
        color === "light"
            ? wacToken("color.neutral.light0")
            : wacToken("color.primary.black");
    const inlineStart =
        textAlign === "left"
            ? "199.5px"
            : textAlign === "right"
            ? "-140px"
            : "0";
    const alignItems =
        textAlign === "left"
            ? "flex-start"
            : textAlign === "right"
            ? "flex-end"
            : "center";

    const justifyContent =
        textAlign === "left"
            ? "flex-start"
            : textAlign === "right"
            ? "flex-end"
            : "center";

    const containerStyle = css({
        animation: "fadein 0.3s ease-in-out",
        background: `url(${backgroundImgSrc?.xs}) 50% / cover no-repeat`,
        maxWidth: "1368px",
        height: "530px",
        flexShrink: "0",
        position: "relative",
        margin: "60px auto 0px",
        padding: "0 32px",
        display: "flex",
        justifyContent: justifyContent,
        alignItems: "center",
        [media.only.xs]: {
            justifyContent: textAlign === "right" ? "center" : justifyContent,
            background:
                textAlign === "right"
                    ? `url(${backgroundImgSrc?.xs}) 50% / 100% 100% no-repeat`
                    : `url(${backgroundImgSrc?.sm}) 50% / cover no-repeat`
        },
        [media.above.xs]: {
            padding: "0 60px"
        },
        [media.only.sm]: {
            background: `url(${backgroundImgSrc?.md}) 50% / cover no-repeat`
        },
        [media.only.md]: {
            background: `url(${backgroundImgSrc?.lg}) 50% / cover no-repeat`
        },
        [media.above.lg]: {
            background: `url(${backgroundImgSrc?.lg}) 50% / cover no-repeat`
        }
    });

    const { md, lg } = contentMaxWidth[textAlign];

    const contentStyle = css({
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: textAlign,
        alignItems: alignItems,
        margin: textAlign === "center" ? "0 auto" : "0",
        textAlign: textAlign === "center" ? "center" : "left",
        maxWidth: lg,
        [media.only.xs]: {
            width: "365px",
            textAlign: textAlign === "right" ? "center" : textAlign,
            alignItems: textAlign === "right" ? "center" : textAlign
        },
        [media.above.xs]: {
            textAlign: textAlign
        },
        [media.only.sm]: {
            minWidth: md
        },
        [media.only.md]: {
            minWidth: lg
        },
        [media.above.md]: {
            insetInlineStart: inlineStart
        },
        [media.above.lg]: {
            minWidth: textAlign === "right" ? "668px" : "568px"
        }
    });

    const headerStyle = css({
        fontFamily: wacToken("font.charlie.display"),
        fontSize: textAlign === "right" ? "10vw" : "44px",
        fontStyle: "normal",
        fontWeight: "800",
        lineHeight: textAlign === "right" ? "12vw" : "50px",
        margin: textAlign === "right" ? "0 0 5vw 0" : "0 0 28px 0",
        color: textColor,
        [media.above.xs]: {
            lineHeight: "50px",
            fontSize: "44px",
            margin: "0 0 28px 0"
        },
        [media.only.xs]: {
            textAlign: textAlign === "right" ? "center" : textAlign
        },
        [media.above.sm]: {
            lineHeight: "56px",
            fontSize: "48px",
            margin: "0 0 16px 0"
        }
    });

    const bodyStyle = css({
        fontFamily: wacToken("font.charlie.text"),
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "24px",
        height: "fit-content",
        maxHeight: "72px",
        width: "fit-content",
        margin: "0 0 28px 0",
        color: textColor,
        [media.only.xs]: {
            textAlign: textAlign === "right" ? "center" : textAlign
        },
        [media.above.xs]: {
            minWidth: "422px",
            margin: "0 0 32px 0"
        },
        [media.above.sm]: {
            maxWidth: "fit-content",
            fontSize: "20px",
            lineHeight: "28px"
        }
    });

    const ctaStyle = css({
        display: "flex",
        alignSelf: alignItems,
        [media.below.xs]: {
            alignSelf: textAlign === "center" ? "center" : "flex-start"
        },
        [media.only.xs]: {
            alignSelf: textAlign === "right" ? "center" : alignItems
        }
    });

    const CallToAction = () => {
        if (!cta) return null;

        if (ctaType === "button") {
            const { text, href, dataEventProps } = cta as {
                text: string;
                href: string;
                dataEventProps: DataEventProps;
            };
            return (
                <Adg4Button
                    css={ctaStyle}
                    variant="primary"
                    size="small"
                    color="yellow"
                    href={href}
                    dataEventProps={dataEventProps}
                >
                    {text}
                </Adg4Button>
            );
        } else if (ctaType === "link") {
            const { text, href, dataEventProps } = cta as {
                text: string;
                href: string;
                dataEventProps: DataEventProps;
            };
            return (
                // eslint-disable-next-line @atlassian/a11y/anchor-has-content,@atlassian/a11y/link-name
                <Link
                    type="external"
                    href={href}
                    text={text}
                    dataEventProps={dataEventProps}
                />
            );
        }

        return <>{cta}</>;
    };

    return (
        <div css={containerStyle}>
            <div css={contentStyle}>
                <span css={headerStyle} className="enterprise-hero-header-text">
                    {headerText}
                </span>
                <span css={bodyStyle}>{bodyText}</span>
                <CallToAction />
            </div>
        </div>
    );
};
